import {
  Box,
  Button,
  Stack,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  LinearProgress,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { ExtractParams } from '@/types/params';

import { ApiResponse_GetAll_Rule, useGetAllRules } from '@/api/endpoints/rule/useGetAllRules';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { PMPRoutes } from '@/config/routes';

import RowActionsMenu from '@/components/common/RowActionsMenu';
import RuleCreateModal from '@/components/rule/RuleCreateModal';
import RuleDeleteModal from '@/components/rule/RuleDeleteModal';
import RuleUpdateModal from '@/components/rule/RuleUpdateModal';

const TABLE_HEAD = [
  { id: 'id', label: 'ID', sortable: true, width: '10%' },
  { id: 'description', label: 'Description', sortable: true, width: '45%' },
  { id: 'expected_output', label: 'Expected Output', sortable: true, width: '45%' },
];

type Order = 'asc' | 'desc';
type Rule = ApiResponse_GetAll_Rule[0];

function getComparator<Key extends keyof Rule>(
  order: Order,
  orderBy: Key
): (a: Rule, b: Rule) => number {
  return (a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    if (order === 'asc') {
      return aValue < bValue ? -1 : 1;
    }
    return bValue < aValue ? -1 : 1;
  };
}

const PropertyLocationViewRulesSubpage: React.FC = () => {
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Rule>('description');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isCreateRuleModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateRuleModalOpen, setIsUpdateRuleModalOpen] = useState(false);
  const [isDeleteRuleModalOpen, setIsDeleteRuleModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<Rule | null>(null);
  const [ruleToUpdate, setRuleToUpdate] = useState<Rule | null>(null);
  const { id } = useParams<ExtractParams<PMPRoutes.propertyLocationViewRuleList>>();
  const location = useLocation();
  const theme = useTheme();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openAddRule') === 'true') {
      setIsCreateModalOpen(true);
    }
  }, [location]);

  const getAllRulesQuery = useGetAllRules({ location_id: Number(id) });

  const handleRequestSort = (property: keyof Rule) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>, _: React.ReactNode) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Filter and sort the data
  const filteredData = useMemo(() => {
    let filtered = getAllRulesQuery.data || [];

    // Apply name filter
    if (filterName) {
      filtered = filtered.filter(
        item =>
          item.description?.toLowerCase().includes(filterName.toLowerCase()) ||
          item.expected_output?.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    // Sort the data
    return [...filtered].sort(getComparator(order, orderBy));
  }, [getAllRulesQuery.data, filterName, order, orderBy]);

  // Get the current page of data
  const displayedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%', position: 'relative' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '60%' }}>
          Add specific rules for how frequently asked questions about this property should be
          answered. These rules will help ensure consistent and accurate responses.
        </Typography>
        <Button
          variant="contained"
          startIcon={<PlusIcon />}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Add Rule
        </Button>
      </Stack>

      <Card sx={{ bgcolor: 'common.white' }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search rules..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map(headCell => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      borderRight: 'none',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      bgcolor: theme.palette.secondary[200],
                      textTransform: 'none',
                      color: 'text.primary',
                      '& .MuiTableSortLabel-root': {
                        textTransform: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.875rem', sm: 'inherit' },
                        fontWeight: 'bold',
                        color: 'text.primary',
                        '&:hover': {
                          color: 'text.primary',
                        },
                        '&.Mui-active': {
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        },
                      },
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id as keyof Rule)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: 'inherit' },
                          fontWeight: 'bold',
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        {headCell.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    width: '50px',
                    bgcolor: theme.palette.secondary[200],
                    borderRight: 'none',
                  }}
                />
              </TableRow>
            </TableHead>

            <TableBody>
              {getAllRulesQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <LinearProgress sx={{ width: '100%' }} />
                  </TableCell>
                </TableRow>
              )}
              {!getAllRulesQuery.isLoading && displayedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No rules found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!getAllRulesQuery.isLoading &&
                displayedData.length > 0 &&
                displayedData.map(row => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: `${theme.palette.secondary[200]} !important`,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                      },
                    }}
                  >
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.expected_output}</TableCell>
                    <TableCell>
                      <RowActionsMenu
                        items={[
                          {
                            label: 'Edit',
                            onClick: () => {
                              setRuleToUpdate(row);
                              setIsUpdateRuleModalOpen(true);
                            },
                          },
                          {
                            label: 'Delete',
                            onClick: () => {
                              setRuleToDelete(row);
                              setIsDeleteRuleModalOpen(true);
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Card>

      {isCreateRuleModalOpen && (
        <RuleCreateModal
          locationId={Number(id)}
          onClose={() => setIsCreateModalOpen(false)}
          onRuleCreated={() => getAllRulesQuery.refetch()}
        />
      )}

      {isUpdateRuleModalOpen && ruleToUpdate && (
        <RuleUpdateModal
          rule={ruleToUpdate}
          onClose={() => {
            setIsUpdateRuleModalOpen(false);
            setRuleToUpdate(null);
          }}
          onRuleUpdated={() => getAllRulesQuery.refetch()}
        />
      )}

      {isDeleteRuleModalOpen && ruleToDelete && (
        <RuleDeleteModal
          rule={ruleToDelete}
          onClose={() => {
            setIsDeleteRuleModalOpen(false);
            setRuleToDelete(null);
          }}
          onRuleDeleted={() => getAllRulesQuery.refetch()}
        />
      )}
    </Stack>
  );
};

export default PropertyLocationViewRulesSubpage;
